import React from 'react';
import { FabrikUser } from '../../../types';
import useCheckedInToday from '../hooks/useCheckedInToday';

interface Props {
  token: string | null;
  selectedSpaceId: string;
  allUsers: FabrikUser[];
  selectedSpaceName?: string;
  spaceIdToName?: Record<string, string>;
  usersReady?: boolean;
  onUserClick?: (userId: string) => void;
}

const CheckedInUsersPanel: React.FC<Props> = ({ token, selectedSpaceId, allUsers, spaceIdToName, usersReady = true, onUserClick }) => {
  const { data, isLoading, error, refresh } = useCheckedInToday(token, selectedSpaceId, allUsers, usersReady);

  const colsClass = selectedSpaceId ? 'cols-4' : 'cols-5';

  return (
    <div className="checkedin-panel">
      <div className="checkedin-header">
        <button className="checkedin-refresh refresh-button" onClick={() => refresh()} disabled={isLoading}>
          {isLoading ? 'Refreshing…' : 'Refresh'}
        </button>
      </div>

      {!usersReady && <div className="checkedin-status">Waiting for users to load…</div>}
      {error && <div className="checkedin-status">{error}</div>}
      {usersReady && isLoading && !data.length && <div className="checkedin-status">Loading today’s check-ins…</div>}
      {usersReady && !isLoading && !error && data.length === 0 && <div className="checkedin-status">No check-ins yet today.</div>}

      {usersReady && data.length > 0 && (
        <div className="checkedin-cards">
          {data.map(item => {
            const rawType = (item.used_quota_type || '').toString().toLowerCase().trim();
            const norm = rawType.replace(/[_-]+/g, ' ');
            const key = norm.includes('member') ? 'membership' : (norm.includes('day') ? 'day' : '');
            const badgeTypeClass = key ? `ci-badge-${key}` : '';
            const display = key === 'membership' ? 'Member Day' : key === 'day' ? 'Day Pass' : (item.used_quota_type || '—');
            const time = new Date(item.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
            return (
              <div 
                key={item.id} 
                className={`checkedin-card ${onUserClick ? 'checkedin-card-clickable' : ''}`}
                onClick={() => onUserClick?.(item.user_id)}
                role={onUserClick ? 'button' : undefined}
                tabIndex={onUserClick ? 0 : undefined}
                onKeyDown={(e) => {
                  if (onUserClick && (e.key === 'Enter' || e.key === ' ')) {
                    e.preventDefault();
                    onUserClick(item.user_id);
                  }
                }}
              >
                <div className="ci-row">
                  <div className="ci-primary">
                    <div className="ci-name" title={item.userName}>{item.userName}</div>
                    <div className="ci-email" title={item.userEmail}>{item.userEmail || '—'}</div>
                  </div>
                  <div className="ci-side">
                    <span className="ci-time" title={time}>{time}</span>
                    <span className="ci-type">
                      <span className={`ci-badge ${badgeTypeClass}`}>{display}</span>
                    </span>
                    {item.notes_count !== undefined && item.notes_count > 0 && (
                      <span className="ci-notes" title={`${item.notes_count} note${item.notes_count !== 1 ? 's' : ''}`}>
                        📝 {item.notes_count}
                      </span>
                    )}
                  </div>
                </div>
                {!selectedSpaceId && (
                  <div className="ci-space" title={spaceIdToName?.[item.space_id] || item.space_id}>
                    {spaceIdToName?.[item.space_id] || item.space_id}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default CheckedInUsersPanel;
